import axios, { Method } from 'axios'
import { useCallback } from 'react'
import { useCookie } from 'web-ui/dist/helpers/hooks/use-cookie'
import { getToken } from 'web-fns/dist/auth'

enum Category {
  Favorites = 'favorites',
  Hidden = 'hidden',
}

/**
 * Abstracted Axios call with common error and response handling.
 */
const makeRequest = async (method: Method, url: string, token: string, data?: object) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'vaas-x-app-id': window.env && window.env.CLIENT_APP_ID,
  }

  try {
    const response = await axios({
      data,
      headers,
      method,
      url,
    })

    if (response.data.statusCode === 200 || response.data.statusCode === 201) {
      return response.data.response
    }

    return {
      error: response.data.message || response.data.status,
    }
  } catch (error) {
    return { error: (error as Error).message }
  }
}

/**
 * Provides hooks to perform CRUD against the productize API.
 */
export const useProductize = (
  BRAND_BASE_URL,
  brandUuid: string | undefined,
  refetch: (() => void) | undefined,
  dataType?: string,
  id?: string
) => {
  const { getValue: getJwtTokenFromCookie } = useCookie('vaas_jwt_auth0')

  const getProducts = useCallback(
    async () => {
      const token = getJwtTokenFromCookie() || getToken()

      if (!brandUuid || !token) {
        console.error(
          `brand defined ${brandUuid !== undefined} token defined ${token !== undefined}`
        )
        return []
      }

      const url = `${BRAND_BASE_URL}/api/productize/brands/${brandUuid}/${dataType}`
      return makeRequest('get', url, token)
    },
    [getJwtTokenFromCookie, makeRequest]
  )

  const setPreference = useCallback(
    (method, category, afterSave?: () => void) => {
      const token = getJwtTokenFromCookie() || getToken()

      if (!token) {
        console.error('No token available to set preferences with')
        return
      }
      if (!brandUuid || !id) {
        console.error('No ID info available to set preferences with')
        return
      }

      const url = `${BRAND_BASE_URL}/api/productize/brands/${brandUuid}/${category}/${dataType}/${id}`
      makeRequest(method, url, token, {}).then(() => {
        if (refetch) {
          refetch()
        }
        if (afterSave) {
          afterSave()
        }
      })
    },
    [getJwtTokenFromCookie, makeRequest]
  )

  const setFeatured = useCallback(
    (afterSave?: () => void) => setPreference('post', Category.Favorites, afterSave),
    [setPreference]
  )

  const setUnfeatured = useCallback(
    (afterSave?: () => void) => setPreference('delete', Category.Favorites, afterSave),
    [setPreference]
  )

  const setHidden = useCallback(
    (afterSave?: () => void) => setPreference('post', Category.Hidden, afterSave),
    [setPreference]
  )

  const setUnhidden = useCallback(
    (afterSave?: () => void) => setPreference('delete', Category.Hidden, afterSave),
    [setPreference]
  )

  return { getProducts, setFeatured, setUnfeatured, setHidden, setUnhidden }
}



// WEBPACK FOOTER //
// ./src/hooks/useProductize.ts